import { useEffect, useLayoutEffect, useMemo, useState, useCallback } from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { applyThemeBrandTokens, resetThemeBrandTokens } from '@digital-twins/deck-ui/foundations/theme/applyThemeBrandTokens'
import type { BrandSettings } from '@digital-twins/shared-types'

import { Sidebar } from '@/components/layout/Sidebar'
import { MobileNavBar } from '@/components/layout/MobileNavBar'
import { GuestNavGuardModal } from '@/components/layout/GuestNavGuardModal'
import { useAppShellChromeLayout } from '@/components/layout/useAppShellChromeLayout'
import { cn } from '@/lib/utils'
import { resolveLegacyFairgenLogoUrl } from '@/lib/theme/fairgen-default-brand'
import { Text } from '@/components/ui/Text'
import { FeedbackProvider } from '@/lib/feedback'
import { NotificationsProvider } from '@/contexts/NotificationsContext'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  SIDEBAR_DESKTOP_COLLAPSED_WIDTH,
  SIDEBAR_DESKTOP_EXPANDED_WIDTH,
  SIDEBAR_MOBILE_WIDTH,
} from '@/components/layout/sidebar.constants'
import { useTheme } from '@/lib/theme-provider'
import {
  isThemeFontId,
  isThemeHeadingFontId,
  isThemePresetId,
  sanitizeThemeOverrides,
  type ThemeFontId,
  type ThemeHeadingFontId,
  type ThemePresetId,
} from '@/lib/theme/theme-presets'
import { isFairgenTwinsDefaultBrand } from '@/lib/theme/fairgen-default-brand'
import { publicApi, publicRoomsApi, type ShareOwnerBrandBundle } from '@/lib/api'

const sidebarTransition = { duration: 0.55, ease: [0.32, 0.72, 0, 1] as const }

const FAIRGEN_DEFAULT_FOOTER_TEXT = 'This report was created using Fairgen'

/**
 * Shared (guest) chrome for `/share/:token` and `/share/room/:token`.
 *
 * When the share's owner-org has the `whiteLabel` flag enabled, the layout
 * switches into an immersive full-width branded experience:
 *  - no guest sidebar, no mobile nav, no `GuestNavGuardModal` mount
 *  - minimal branded header bar (org logo + study name)
 *  - report renders full-width
 *  - footer line is the org's `shareFooterText` (with `{OrgName}` interpolation),
 *    or omitted entirely when the field is empty/whitespace
 *
 * Theming during the share view: the layout briefly takes over the
 * `ThemeProvider`'s preset/font/overrides for the duration of mount, then
 * restores the viewer's previous selections on unmount. This means a guest
 * who is also a logged-in operator with their own preset selection will see
 * the share owner's branding while on the share page, and snap back to
 * their own theme when they navigate away.
 *
 * When `whiteLabel` is OFF, behavior is byte-stable with the previous
 * implementation: same guest sidebar, same `GuestNavGuardModal`, same
 * Fairgen-attributed footer. Deck-specific branding is intentionally not
 * applied here; `DeckViewer` owns deck-scope token writes from deck metadata.
 */
export function SharedAppShellLayout() {
  const { pathname } = useLocation()
  const params = useParams<{ token?: string }>()
  const token = params.token ?? ''
  const isRoomShare = pathname.startsWith('/share/room/')
  const isAudienceInvite = pathname.startsWith('/audiences/invites/')

  const {
    mounted,
    isCollapsed,
    mobileNavOpen,
    setMobileNavOpen,
    toggleSidebar,
  } = useAppShellChromeLayout(pathname)
  const effectiveIsCollapsed = isAudienceInvite || isCollapsed
  const effectiveToggleSidebar = isAudienceInvite ? () => undefined : toggleSidebar

  const [guardModalOpen, setGuardModalOpen] = useState(false)
  const [studyName, setStudyName] = useState<string>('')
  const [branding, setBranding] = useState<ShareOwnerBrandBundle | null>(null)

  useEffect(() => {
    // Audience invite routes share this layout but don't have a study/room share
    // token — the :token param is the invite UUID. Skip getAccessMode for them
    // to avoid spurious 401s in the console.
    if (!token || isAudienceInvite) return
    let cancelled = false
    async function load() {
      try {
        const result = isRoomShare
          ? await publicRoomsApi.getAccessMode(token)
          : await publicApi.getAccessMode(token)
        if (cancelled) return
        setBranding(result.branding ?? null)
      } catch {
        if (!cancelled) setBranding(null)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token, isRoomShare, isAudienceInvite])

  const whiteLabel = branding?.whiteLabel === true

  useShareOwnerTheme(whiteLabel ? branding : null)

  const setStudyNameStable = useCallback((name: string) => {
    setStudyName(name)
  }, [])

  const outletContext = useMemo(
    () => ({
      branding,
      whiteLabel,
      setStudyName: setStudyNameStable,
    }),
    [branding, whiteLabel, setStudyNameStable],
  )

  if (!mounted) {
    return <div className="min-h-screen bg-background" />
  }

  if (whiteLabel && branding) {
    return <WhiteLabelShareShell branding={branding} studyName={studyName} outletContext={outletContext} />
  }

  return (
    <FeedbackProvider>
      <NotificationsProvider>
        <div className="flex min-h-screen bg-background">
          <motion.aside
            role="navigation"
            aria-label="Main navigation"
            initial={false}
            animate={{
              width: effectiveIsCollapsed
                ? SIDEBAR_DESKTOP_COLLAPSED_WIDTH
                : SIDEBAR_DESKTOP_EXPANDED_WIDTH,
            }}
            transition={sidebarTransition}
            className="hidden md:block fixed inset-y-0 left-0 z-40 bg-background overflow-hidden border-r border-sidebar-border dark:border-card-border"
          >
            <Sidebar
              isCollapsed={effectiveIsCollapsed}
              toggleSidebar={effectiveToggleSidebar}
              guestMode
              onGuestNavClick={() => setGuardModalOpen(true)}
            />
          </motion.aside>

          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="left"
              className="p-0 bg-background border-sidebar-border overflow-hidden"
              style={{ width: SIDEBAR_MOBILE_WIDTH }}
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <Sidebar
                isCollapsed={false}
                toggleSidebar={() => setMobileNavOpen(false)}
                guestMode
                onGuestNavClick={() => setGuardModalOpen(true)}
              />
            </SheetContent>
          </Sheet>

          <div
            style={{
              '--desktop-sidebar-width': `${effectiveIsCollapsed ? SIDEBAR_DESKTOP_COLLAPSED_WIDTH : SIDEBAR_DESKTOP_EXPANDED_WIDTH}px`,
            } as React.CSSProperties}
            className={cn(
              'flex flex-1 flex-col min-w-0 transition-[padding] duration-500',
              'md:pl-[var(--desktop-sidebar-width)]',
            )}
          >
            <MobileNavBar onOpenMenu={() => setMobileNavOpen(true)} />
            <main className="flex-1 w-full">
              <Outlet context={outletContext} />
            </main>
            {!pathname.startsWith('/share/room/') && (
              <footer className="border-t border-border bg-[hsl(var(--report-bg))]">
                <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-center">
                  <Text size="xs" color="muted">
                    This report was created using{' '}
                    <a
                      href="https://www.fairgen.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-foreground hover:underline"
                    >
                      Fairgen
                    </a>
                  </Text>
                </div>
              </footer>
            )}
          </div>
        </div>

        <GuestNavGuardModal
          open={guardModalOpen}
          onOpenChange={setGuardModalOpen}
        />
      </NotificationsProvider>
    </FeedbackProvider>
  )
}

interface WhiteLabelShareShellProps {
  branding: ShareOwnerBrandBundle
  studyName: string
  outletContext: SharedShellOutletContext
}

function WhiteLabelShareShell(props: WhiteLabelShareShellProps) {
  const { branding, studyName, outletContext } = props
  const { resolvedTheme } = useTheme()

  const orgName = branding.orgName ?? ''
  const brandLogoUrl = resolveLegacyFairgenLogoUrl(branding.brandLogoUrl)
  const brandLogoUrlOnDark = resolveLegacyFairgenLogoUrl(branding.brandLogoUrlOnDark)
  const logoForTheme =
    resolvedTheme === 'dark' && brandLogoUrlOnDark
      ? brandLogoUrlOnDark
      : (brandLogoUrl ?? brandLogoUrlOnDark ?? null)

  const footerText = renderShareFooter(branding.shareFooterText, orgName)

  return (
    <FeedbackProvider>
      <NotificationsProvider>
        <div className="flex min-h-screen flex-col bg-background">
          <header
            data-testid="share-branded-header"
            className="sticky top-0 z-40 flex h-14 w-full items-center justify-between gap-4 border-b border-border bg-card px-6"
          >
            <div className="flex min-w-0 flex-1 items-center gap-3">
              {logoForTheme ? (
                <img
                  src={logoForTheme}
                  alt={orgName || 'Organization logo'}
                  data-testid="share-branded-header-logo"
                  className="h-8 max-h-10 w-auto object-contain"
                />
              ) : orgName ? (
                <span
                  data-testid="share-branded-header-orgname"
                  className="text-sm font-semibold text-foreground"
                >
                  {orgName}
                </span>
              ) : null}
            </div>
            {studyName && (
              <div className="flex min-w-0 flex-1 items-center justify-end">
                <span
                  data-testid="share-branded-header-study-name"
                  className="truncate text-sm font-medium text-foreground"
                  title={studyName}
                >
                  {studyName}
                </span>
              </div>
            )}
          </header>

          <main className="flex-1 w-full">
            <Outlet context={outletContext} />
          </main>

          {footerText && (
            <footer
              data-testid="share-footer-text"
              className="border-t border-border bg-card py-4 text-center text-xs text-muted-foreground"
            >
              {footerText}
            </footer>
          )}
        </div>
      </NotificationsProvider>
    </FeedbackProvider>
  )
}

/** Apply the share owner's preset/font/overrides for the duration of the
 *  shared view, then restore whatever the viewer had selected before. */
function useShareOwnerTheme(branding: ShareOwnerBrandBundle | null) {
  const {
    presetId: viewerPreset,
    setPresetId,
    fontFamily: viewerFont,
    setFontFamily,
    headingFontFamily: viewerHeadingFont,
    setHeadingFontFamily,
    themeOverrides: viewerOverrides,
    setThemeOverrides,
    resolvedTheme,
  } = useTheme()

  useLayoutEffect(() => {
    if (!branding) return

    const previousPreset: ThemePresetId | null = viewerPreset
    const previousFont: ThemeFontId | null = viewerFont
    const previousHeadingFont: ThemeHeadingFontId | null = viewerHeadingFont
    const previousOverrides = viewerOverrides

    if (isThemePresetId(branding.themePresetId)) {
      setPresetId(branding.themePresetId)
    } else {
      setPresetId(null)
    }

    if (isThemeFontId(branding.brandFontFamily)) {
      setFontFamily(branding.brandFontFamily)
    } else {
      setFontFamily(null)
    }

    if (isThemeHeadingFontId(branding.brandHeadingFontFamily)) {
      setHeadingFontFamily(branding.brandHeadingFontFamily)
    } else {
      setHeadingFontFamily(null)
    }

    setThemeOverrides(sanitizeThemeOverrides(branding.themeOverrides ?? {}))

    return () => {
      setPresetId(previousPreset)
      setFontFamily(previousFont)
      setHeadingFontFamily(previousHeadingFont)
      setThemeOverrides(previousOverrides)
    }
    // Intentional closure capture: we snapshot the viewer's pre-share theme
    // exactly once per `branding` payload (mount + branding-change). Adding
    // `viewer*` setters to deps would (a) cause infinite re-runs because we
    // mutate the same state we depend on, and (b) overwrite the snapshot on
    // every render — the restore-on-unmount would then restore the share
    // owner's theme instead of the viewer's. Cross-tab changes made while a
    // share page is open may restore to this pre-share snapshot; that tradeoff
    // is accepted so the share route can reliably restore local navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branding])

  useLayoutEffect(() => {
    if (!branding) return

    const brandSettings: BrandSettings = {
      brandPrimaryColor: branding.brandPrimaryColor,
      brandSecondaryColor: branding.brandSecondaryColor,
      brandThemeConfig: branding.brandThemeConfig,
      brandLogoUrl: resolveLegacyFairgenLogoUrl(branding.brandLogoUrl),
      brandLogoUrlOnDark: resolveLegacyFairgenLogoUrl(branding.brandLogoUrlOnDark),
      brandIconLogoUrl: branding.brandIconLogoUrl,
      brandDomain: null,
      brandRoleGraph: branding.brandRoleGraph,
      brandRoleGraphVersion: branding.brandRoleGraphVersion,
      brandRoleGraphHash: branding.brandRoleGraphHash,
      themePresetId: isThemePresetId(branding.themePresetId)
        ? branding.themePresetId
        : null,
      brandFontFamily: isThemeFontId(branding.brandFontFamily)
        ? branding.brandFontFamily
        : null,
      brandHeadingFontFamily: isThemeHeadingFontId(branding.brandHeadingFontFamily)
        ? branding.brandHeadingFontFamily
        : null,
      themeOverrides: branding.themeOverrides,
      shareFooterText: branding.shareFooterText,
    }

    if (isFairgenTwinsDefaultBrand(brandSettings)) {
      resetThemeBrandTokens('full')
      return
    }

    return applyThemeBrandTokens(brandSettings, resolvedTheme, {
      scope: 'full',
      restore: true,
    })
  }, [branding, resolvedTheme])
}

/**
 * Resolve the share footer line. Empty / whitespace-only stored values
 * yield `null` (no footer). The `{OrgName}` token is interpolated to the
 * owner-org name so the default copy reads naturally. Returns `null` if
 * after interpolation the line is empty.
 */
function renderShareFooter(
  shareFooterText: string | null,
  orgName: string,
): string | null {
  if (shareFooterText === null || shareFooterText === undefined) {
    return null
  }
  const interpolated = shareFooterText.replace(/\{OrgName\}/g, orgName)
  const trimmed = interpolated.trim()
  if (trimmed.length === 0) {
    return null
  }
  // Strip leading/trailing whitespace so the centered footer renders cleanly
  // regardless of how an admin entered the text in Settings (no stray
  // indentation, no trailing newlines).
  return trimmed
}

/**
 * Outlet context exposed to share child pages so they can pass the
 * report/study display name up to the branded header bar.
 *
 * `branding === null` while the access-mode response is still in flight;
 * `whiteLabel === false` when the share's owner-org doesn't have the
 * `whiteLabel` flag (children should fall back to the existing
 * Fairgen-attributed chrome).
 */
export interface SharedShellOutletContext {
  branding: ShareOwnerBrandBundle | null
  whiteLabel: boolean
  setStudyName: (name: string) => void
}

/**
 * Compute the document title for a share page.
 *
 *  - whiteLabel ON  + reportName + orgName  → "${reportName} · ${orgName}"
 *  - whiteLabel ON  + reportName, no orgName → reportName
 *  - whiteLabel OFF                         → null (caller falls through to
 *                                             the host's default <title>)
 *  - empty/missing reportName               → null
 */
export function computeShareTitle(
  whiteLabel: boolean,
  reportName: string | null | undefined,
  orgName: string | null | undefined,
): string | null {
  const trimmedName = (reportName ?? '').trim()
  if (!whiteLabel || trimmedName.length === 0) return null
  const trimmedOrg = (orgName ?? '').trim()
  return trimmedOrg.length > 0 ? `${trimmedName} · ${trimmedOrg}` : trimmedName
}

export { FAIRGEN_DEFAULT_FOOTER_TEXT, renderShareFooter }
